import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { UtilityService } from '../utility.service';
import { Vendor } from 'app/models/Vendor';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';
import { environment } from 'environments/environment';
import { VendorPaymentValidation } from 'app/models/form/VendorPaymentValidation';
import { DynamicFormUtilityServices } from '../dynamic-form-utility.service';
import { DuplicateVendorView } from 'app/models/form/DuplicateVendorView';
import { VendorOverwriteCandidatesView } from 'app/models/form/VendorOverwriteCandidatesView';
import { LookupServices } from '../lookup.service';

@Injectable({
	providedIn: 'root',
})
export class VendorFormService {
	private readonly urlPath = 'vendor';

	constructor(
		private readonly httpClient: HttpClient,
		private readonly utilityService: UtilityService,
		private readonly dynamicFormUtilityServices: DynamicFormUtilityServices,
		private readonly lookupServices: LookupServices
	) {}

	createForm(clientId: string, request: any, prefix: string): Observable<Vendor> {
		return this.httpClient
			.post<Vendor>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}`,
				this.setupRequest(request, prefix)
			)
			.pipe(tap(() => this.lookupServices.clearVendorLookupCache()));
	}

	private setupRequest(request: any, prefix: string) {
		const formData: FormData = new FormData();
		formData.append('vendor W9', request.vendorW9File);
		formData.append('voided Check', request.voidedCheckFile);
		formData.append('model', JSON.stringify(request));
		for (let i = 0; i < request.attachments.length; i++) {
			const name = `additional file -${i + 1}`;
			formData.append(name, request.attachments[i].file);
			request.attachments[i].name = name;
		}

		//Append Addtional fields Files
		this.dynamicFormUtilityServices.appendDocuments(formData, prefix, request);

		return formData;
	}

	updateForm(clientId: string, formId: string, request: any, prefix: string): Observable<any> {
		return this.httpClient
			.put<any>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}`,
				this.setupRequest(request, prefix)
			)
			.pipe(tap(() => this.lookupServices.clearVendorLookupCache()));
	}

	removeDocument(clientId: string, formId: string, documentId: string): Observable<any> {
		return this.httpClient
			.delete<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/document/${documentId}`)
			.pipe();
	}

	getForm(id: string, clientId: string): Observable<Vendor> {
		return this.httpClient.get<Vendor>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}`).pipe();
	}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${environment.apiUrl}forms/clients/${clientId}/${
			this.urlPath
		}/users/${userId}?${this.utilityService.objectToQueryString(search)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	rejectForm(
		clientId: string,
		formId: string,
		rejectionReason: { reason: string; workflowId: number }
	): Observable<Vendor> {
		return this.httpClient
			.post<Vendor>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/decline`, rejectionReason)
			.pipe();
	}

	approveForm(clientId: string, formId: string, userId: string): Observable<Vendor> {
		return this.httpClient
			.post<Vendor>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/approve`, {
				userId,
			})
			.pipe(tap(() => this.lookupServices.clearVendorLookupCache()));
	}

	sendBack(clientId: string, formId: string): Observable<Vendor> {
		return this.httpClient
			.post<Vendor>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/review`, {})
			.pipe();
	}

	exportToPdf(clientId: string, formId: string): any {
		return this.httpClient
			.get(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${formId}/export`, {
				responseType: 'blob',
			})
			.pipe();
	}

	getVendors(clientId: string, filter: string): Observable<Vendor[]> {
		return this.httpClient
			.get<Vendor[]>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/GetAllVendors/${filter}`)
			.pipe();
	}

	exportVendors(clientId: string, filter: string, addressColumns: string[] = []): Observable<Blob> {
		const url = `${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/export/${filter}`;
		if (addressColumns.length === 0) {
			return this.httpClient.get(url, { responseType: 'blob' }).pipe();
		}
		// The enabled address columns go in a body: there can be more of them than a query string holds.
		return this.httpClient.post(url, { addressColumns }, { responseType: 'blob' }).pipe();
	}

	deleteForm(clientId: string, id: string) {
		return this.httpClient
			.delete(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}`)
			.pipe(tap(() => this.lookupServices.clearVendorLookupCache()));
	}

	submitForApproval(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/approval`, {
				userId,
			})
			.pipe();
	}

	uploadFile(clientId: string, id, file: File): Observable<HttpEvent<any>> {
		const formData: FormData = new FormData();
		formData.append('file', file);
		const req = new HttpRequest(
			'POST',
			`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/docs`,
			formData,
			{
				reportProgress: true,
				responseType: 'json',
			}
		);
		return this.httpClient.request(req);
	}

	validatePayment(clientId: string, vendorId: string): Observable<VendorPaymentValidation> {
		return this.httpClient
			.get<VendorPaymentValidation>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${vendorId}/validate-payment`
			)
			.pipe();
	}
	CheckVendorPortalShowDiscountOption(clientId: string): Observable<boolean> {
		return this.httpClient
			.get<boolean>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/check-show-discount-option`)
			.pipe();
	}
	importVendors(
		clientId: string,
		data: any,
		sendConfirmation = false,
		isDuplicate = false
	): Observable<HttpEvent<any>> {
		return this.httpClient
			.post<any>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/import-vendor?sendConfirmation=${sendConfirmation}&isDuplicate=${isDuplicate}`,
				data
			)
			.pipe(tap(() => this.lookupServices.clearVendorLookupCache()));
	}
	checkDuplicateVendors(
		clientId: string,
		params: { id: string; socialSecurityNumber: string; employerIdentificationNumber: string }[]
	): Observable<DuplicateVendorView[]> {
		return this.httpClient
			.post<DuplicateVendorView[]>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/check-duplicates-vendors`,
				params
			)
			.pipe();
	}
	getImportOverwriteCandidates(
		clientId: string,
		params: { socialSecurityNumber: string; employerIdentificationNumber: string }[]
	): Observable<VendorOverwriteCandidatesView[]> {
		return this.httpClient
			.post<VendorOverwriteCandidatesView[]>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/import-overwrite-candidates`,
				params
			)
			.pipe();
	}

	emailForm(clientId: string, formId: string, blob: Blob, filename?: string): Observable<any> {
		const formData = new FormData();
		formData.append('file', blob, filename);

		return this.httpClient
			.post<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/email-form/${formId}`, formData)
			.pipe();
	}
	shareform(clientId: string, childclientId: string, vendorid: string): Observable<Vendor> {
		return this.httpClient
			.get<Vendor>(
				`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/shareForm/${vendorid}/${childclientId}`
			)
			.pipe();
	}
	areAllVendorEmailsValid(clientId: string, id: string): Observable<boolean> {
		return this.httpClient
			.get<boolean>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/areAllVendorEmailsValid/${id}`)
			.pipe();
	}
	delegateForm(clientId: string, id: any, userId: string): Observable<any> {
		return this.httpClient
			.put<any>(`${environment.apiUrl}forms/clients/${clientId}/${this.urlPath}/${id}/delegate/${userId}`, {})
			.pipe();
	}
}
